import * as React from 'react';
import Paper from '@material-ui/core/Paper';
import {
    Chart,
    AreaSeries,
    BarSeries,
    SplineSeries,
    ScatterSeries,
    ArgumentAxis,
    ValueAxis,
    Title,
    Legend,
    Tooltip,
} from '@devexpress/dx-react-chart-material-ui';

import {
    ValueScale,
    Stack,
    Animation,
    EventTracker,
    HoverState,
} from '@devexpress/dx-react-chart';
import { connectProps } from '@devexpress/dx-react-core';
import { format } from 'd3-format';

const consumptionSeriesName = 'Taxa de Permanência';
const consumptionColor = '#f5481d';
const priceColor = 'gray';

const makeLabel = (symbol, color) => ({ text, style, ...restProps }) => (
    <ValueAxis.Label
        text={`${text} ${symbol}`}
        style={{
            fill: color,
            ...style,
        }}
        {...restProps}
    />
);
const LabelWithThousand = makeLabel('%', priceColor);

const patchProps = ({ hoverIndex, ...props }) => ({
    state: props.index === hoverIndex ? 'hovered' : null,
    ...props,
});

const pointOptions = { size: 7 };

const SplinePoint = props => (
    <ScatterSeries.Point point={pointOptions} {...patchProps(props)} />
);

const SplineWithPoints = props => (
    <React.Fragment>
        <SplineSeries.Path {...props} />
        <ScatterSeries.Path {...props} />
    </React.Fragment>
);

const series = [
    {
        name: consumptionSeriesName, key: 'max', color: '#08abbd'
    },
];

const legendRootStyle = {
    display: 'flex',
    margin: 'auto',
    flexDirection: 'row',
};
const LegendRoot = props => (
    <Legend.Root {...props} style={legendRootStyle} />
);

const legendItemStyle = {
    flexDirection: 'column',
    marginLeft: '-2px',
    marginRight: '-2px',
    color: '#000'
};
const LegendItem = props => (
    <Legend.Item {...props} style={legendItemStyle} />
);

const legendLabelStyle = {
    whiteSpace: 'nowrap',
};
const LegendLabel = props => (
    <Legend.Label {...props} style={legendLabelStyle} />
);

const formatTooltip = format('.1f');
const TooltipContent = ({
    data, text, style, ...props
}) => {
    const alignStyle = {
        ...style,
        paddingLeft: '10px',
    };
    const items = series.map(({ name, key, color }) => {
        const val = data[key];
        return (
            <tr key={key}>
                <td>
                    <svg width="10" height="10">
                        <circle cx="5" cy="5" r="5" fill={color} />
                    </svg>
                </td>
                <td>
                    <Tooltip.Content style={alignStyle} text={name} {...props} />
                </td>
                <td align="right">
                    <Tooltip.Content style={alignStyle} text={val ? formatTooltip(val) : 'N/A'} {...props} />
                </td>
            </tr>
        );
    });
    return (
        <table>
            {items}
        </table>
    );
};

const stacks = [
    { series: series.filter(obj => !obj.type).map(obj => obj.name) },
];

const modifyOilDomain = domain => [domain[0], 100];
const modifyPriceDomain = () => [0, 110];

const getHoverIndex = ({ target }) => (target ? target.point : -1);

export default class Demo extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            target: null,
        };

        this.changeHover = target => this.setState({
            target: target ? { series: consumptionSeriesName, point: target.point } : null,
        });

        this.createComponents();
        this.createSeries();
    }

    componentDidUpdate(prevProps, prevState) {
        if (getHoverIndex(prevState) !== getHoverIndex(this.state)) {
            this.SplinePoint.update();
            this.TooltipContent.update();
        }
    }

    createComponents() {
        const getHoverProps = () => ({
            hoverIndex: getHoverIndex(this.state),
        });
        this.SplinePoint = connectProps(SplinePoint, getHoverProps);

        this.TooltipContent = connectProps(TooltipContent, () => {
            const { target } = this.state;
            return { data: target ? this.props.info[target.point] : null };
        });
    }

    createSeries() {
        this.series = series.map(({
            name, key, color, type, scale,
        }) => {
            const props = {
                key: name,
                name,
                valueField: key,
                argumentField: 'year',
                color,
                scaleName: scale || 'oil',
            };
            props.seriesComponent = SplineWithPoints;
            props.pointComponent = this.SplinePoint;
            return React.createElement(type || BarSeries, props);
        });
    }

    render() {
        const { target } = this.state;

        return (
            <Paper>
                <Chart
                    data={this.props.info}
                >
                    <ValueScale
                        name="oil"
                        modifyDomain={modifyOilDomain}
                    />

                    <ArgumentAxis />
                    <ValueAxis
                        scaleName="oil"
                        labelComponent={LabelWithThousand}
                    />

                    <div></div>

                    {this.series}

                    <Animation />
                    <Legend
                        position="bottom"
                        rootComponent={LegendRoot}
                        itemComponent={LegendItem}
                        labelComponent={LegendLabel}
                    />
                    <Stack stacks={stacks} />
                    <EventTracker />
                    <HoverState
                        hover={target}
                        onHoverChange={this.changeHover}
                    />
                    <Tooltip
                        targetItem={target}
                        contentComponent={this.TooltipContent}
                    />
                </Chart>
            </Paper>
        );
    }
}